/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { type LogTarget } from '@amazon/vinyl-util'
import type { AuthTokenProvider } from '@amzn/vinyl-amzn'
import type { Auth, AuthStatus } from '@amzn/amp-playback'
import { logStatusTransition } from '@/auth/authLog'
import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'

/**
 * Shared base for bearer-token auth controllers (CBL, LWA): they all hold a
 * single `AuthTokenProvider`, are signed-in exactly while it is non-null, and
 * drive the same change-guarded status transition. Subclasses supply the
 * initial status and the sign-in/out flows.
 */
export abstract class TokenAuth implements Auth, LogTarget {
    abstract readonly logPrefix: string

    protected _authTokenProvider: AuthTokenProvider | null = null
    protected readonly _status: MutableValue<AuthStatus>

    constructor(initialStatus: AuthStatus) {
        this._status = data(initialStatus)
    }

    get status(): ObservableValue<AuthStatus> {
        return this._status
    }

    isSignedIn(): boolean {
        return this._authTokenProvider != null
    }

    usesBearer(): boolean {
        return true
    }

    get authTokenProvider(): AuthTokenProvider {
        if (this._authTokenProvider == null) throw new Error('not signed in')
        return this._authTokenProvider
    }

    abstract signIn(): void
    abstract signOut(): void

    protected setStatus = (status: AuthStatus) => logStatusTransition(this, this._status, status)
}
